import {
  ArrowLeft,
  ArrowUpRight,
  Building2,
  Check,
  Compass,
  Mail,
  Quote,
  Sparkles,
  UsersRound,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { executivesData, findExecutiveProfile } from "@/data/executives";
import { homeUrl } from "@/lib/assets";

function currentProfileSlug(): string {
  const configured = window.ACAD_REACT_CONFIG?.currentPageSlug;
  if (configured) return configured;

  const parts = window.location.pathname.split("/").filter(Boolean);
  return parts[parts.length - 1] || "perfil-ejecutivo-ricardo-castillo";
}

export default function ExecutiveProfilePage() {
  const profile = findExecutiveProfile(currentProfileSlug());
  const otherExecutives = executivesData.filter((executive) => executive.id !== profile.id);
  const nameParts = profile.name.split(" ");
  const firstName = nameParts.shift() || profile.name;
  const lastName = nameParts.join(" ");

  return (
    <div className="acad-react-page acad-profile-page">
      <Header />

      <main>
        <section className="acad-profile-hero" data-nav="dark">
          <span className="acad-profile-hero-grid" aria-hidden="true" />
          <span className="acad-profile-hero-orbit" aria-hidden="true" />

          <div className="acad-profile-shell acad-profile-hero-inner">
            <a className="acad-profile-back" href={homeUrl("nosotros/#leadership-section")}>
              <ArrowLeft aria-hidden="true" /> Volver al Ejecutivo Nacional
            </a>

            <div className="acad-profile-hero-layout">
              <div className="acad-profile-hero-copy">
                <span className="acad-profile-eyebrow">Perfil Ejecutivo · {profile.number}</span>
                <span className="acad-profile-role">{profile.role}</span>
                <h1>
                  {firstName}
                  {lastName && <span>{lastName}</span>}
                </h1>
                <p>{profile.bio}</p>

                <div className="acad-profile-focus-pills">
                  {profile.focus.map((item) => <span key={item}>{item}</span>)}
                </div>
              </div>

              <figure className="acad-profile-portrait">
                <div className="acad-profile-portrait-frame">
                  <img src={profile.image} alt={profile.name} loading="eager" decoding="async" />
                </div>
                <figcaption>
                  <span>{profile.number}</span>
                  <div>
                    <small>Ejecutivo Nacional</small>
                    <strong>ACAD Costa Rica</strong>
                  </div>
                  <Compass aria-hidden="true" />
                </figcaption>
              </figure>
            </div>
          </div>

          <span className="acad-profile-outline-word" aria-hidden="true">LIDERAZGO</span>
        </section>

        <section className="acad-profile-biography" id="biografia">
          <div className="acad-profile-shell acad-profile-biography-grid">
            <aside>
              <span className="acad-profile-eyebrow acad-profile-eyebrow--dark">Conozca su historia</span>
              <h2>Biografía</h2>
              <div className="acad-profile-quote-mark"><Quote aria-hidden="true" /></div>
            </aside>

            <div className="acad-profile-biography-copy">
              <p className="acad-profile-lead">{profile.bio}</p>
              <p>
                Desde su función como <strong>{profile.role}</strong>, forma parte del trabajo
                conjunto del Ejecutivo Nacional y contribuye al cumplimiento de la misión de las
                Asambleas de Dios de Costa Rica.
              </p>
              <p>
                Este espacio está preparado para ampliar su historia ministerial, llamado,
                formación, familia y principales etapas de servicio conforme se incorpore la
                biografía oficial.
              </p>

              <div className="acad-profile-service-note">
                <Sparkles aria-hidden="true" />
                <div>
                  <small>Liderazgo con propósito</small>
                  <strong>Servir a la iglesia, acompañar a sus líderes y fortalecer la misión.</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="acad-profile-responsibilities" data-nav="dark">
          <div className="acad-profile-shell">
            <header className="acad-profile-section-head">
              <span className="acad-profile-eyebrow">Áreas de servicio</span>
              <div>
                <h2>Una responsabilidad.<br />Tres enfoques clave.</h2>
                <p>
                  Su función dentro del Ejecutivo Nacional se expresa en áreas concretas de
                  acompañamiento, dirección y servicio institucional.
                </p>
              </div>
            </header>

            <div className="acad-profile-focus-grid">
              {profile.focus.map((item, index) => (
                <article key={item}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <i><Check aria-hidden="true" /></i>
                  <h3>{item}</h3>
                  <p>Parte esencial de su aporte al trabajo y la misión del Ejecutivo Nacional.</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="acad-profile-connection">
          <div className="acad-profile-shell acad-profile-connection-card">
            <div className="acad-profile-connection-icon"><Building2 aria-hidden="true" /></div>
            <div>
              <span className="acad-profile-eyebrow acad-profile-eyebrow--dark">Oficina Nacional</span>
              <h2>¿Necesita comunicarse con el Ejecutivo?</h2>
              <p>La Oficina Nacional puede orientar su consulta hacia el área correspondiente.</p>
            </div>
            <a href={homeUrl("contacto/?tema=general#contacto-formulario")}>
              Contactar <Mail aria-hidden="true" />
            </a>
          </div>
        </section>

        <section className="acad-profile-team" id="equipo">
          <div className="acad-profile-shell">
            <header className="acad-profile-team-head">
              <div>
                <span className="acad-profile-eyebrow acad-profile-eyebrow--dark">Ejecutivo Nacional</span>
                <h2>Conozca a los demás integrantes.</h2>
              </div>
              <UsersRound aria-hidden="true" />
            </header>

            <div className="acad-profile-team-grid">
              {otherExecutives.map((executive) => (
                <a href={homeUrl(`${executive.slug}/`)} key={executive.id}>
                  <img src={executive.image} alt={executive.name} loading="lazy" />
                  <div>
                    <small>{executive.role}</small>
                    <strong>{executive.name}</strong>
                  </div>
                  <ArrowUpRight aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
